import { EditorSelection, StateEffect, StateField, Prec } from "@codemirror/state";
import { hasNextSnippetField, selectedCompletion } from "@codemirror/autocomplete";
import { Decoration, EditorView, getTooltip, keymap, showTooltip } from "@codemirror/view";
import { ensureSyntaxTree, syntaxTree } from "@codemirror/language";
import { ancestorOfNodeWithType } from "components/organisms/manuscript/editor/latex/tree/ancestors";

function createTooltipView(update) {
    const dom = document.createElement("div");
    dom.role = "menu";
    dom.classList.add("ol-cm-command-tooltip");
    return { dom, update };
}

function getArgumentNode(commandNode) {
    if (commandNode.type.is("HrefCommand") || commandNode.type.is("UrlCommand")) {
        return commandNode.getChild("UrlArgument")?.getChild("LiteralArgContent");
    }
    if (commandNode.type.is("Ref")) {
        return commandNode.getChild("RefArgument")?.getChild("ShortTextArgument")?.getChild("ShortArg");
    }
    if (commandNode.type.is("Include")) {
        return commandNode.getChild("IncludeArgument")?.getChild("FilePathArgument")?.getChild("LiteralArgContent");
    }
    if (commandNode.type.is("Input")) {
        return commandNode.getChild("InputArgument")?.getChild("FilePathArgument")?.getChild("LiteralArgContent");
    }
    if (commandNode.type.is("Subfile")) {
        return commandNode.getChild("SubfileArgument")?.getChild("FilePathArgument")?.getChild("LiteralArgContent");
    }
    return null;
}

function readArgumentText(state, argumentNode) {
    if (!argumentNode) {
        return "";
    }
    return state.sliceDoc(argumentNode.from, argumentNode.to);
}

function buildTooltip(command, pos, value, commandNode, argumentNode, update) {
    if (!argumentNode) {
        return null;
    }

    const { from, to } = commandNode;

    if (value && from === value.range.from && to === value.range.to) {
        return {
            ...value,
            tooltip: { ...value.tooltip, pos },
        };
    }

    return {
        command,
        range: EditorSelection.range(from, to),
        tooltip: {
            create: () => createTooltipView(update),
            arrow: true,
            pos,
        },
    };
}

function createTooltipState(state, value) {
    const { main } = state.selection;
    const pos = main.head;
    const node = syntaxTree(state).resolveInner(pos, 0);
    const commandNode = ancestorOfNodeWithType(node, "HrefCommand", "UrlCommand", "Ref", "Include", "Input", "Subfile");

    if (!commandNode) {
        return null;
    }

    if (main.from < commandNode.from || main.to > commandNode.to) {
        return null;
    }

    const commandName = commandNode.name;
    let argumentNode = null;
    let update = undefined;

    switch (commandName) {
        case "HrefCommand":
            argumentNode = getArgumentNode(commandNode);
            if (argumentNode && state.sliceDoc(argumentNode.from, argumentNode.to).includes("\n")) {
                return null;
            }
            update = (viewUpdate) => {
                const tooltipState = commandTooltipState(viewUpdate.state);
                const input = tooltipState && firstInteractiveElement(viewUpdate.view, tooltipState);
                if (input && document.activeElement !== input) {
                    const resolvedCommandNode = resolveCommandNode(viewUpdate.state);
                    const resolvedArgumentNode = getArgumentNode(resolvedCommandNode);
                    if (resolvedArgumentNode) {
                        const url = viewUpdate.state.sliceDoc(resolvedArgumentNode.from, resolvedArgumentNode.to);
                        if (url !== input.value) {
                            input.dispatchEvent(
                                new CustomEvent("value-update", {
                                    detail: url,
                                }),
                            );
                        }
                    }
                }
            };
            break;
        case "UrlCommand":
            argumentNode = getArgumentNode(commandNode);
            if (argumentNode) {
                const content = state.sliceDoc(argumentNode.from, argumentNode.to).trim();
                if (!content || content.includes("\n") || !/^https?:\/\/\w+/i.test(content)) {
                    return null;
                }
            }
            break;
        case "Ref":
        case "Include":
        case "Input":
        case "Subfile":
            argumentNode = getArgumentNode(commandNode);
            break;
        default:
            return null;
    }

    const tooltipState = buildTooltip(commandName, pos, value, commandNode, argumentNode, update);
    if (!tooltipState) {
        return null;
    }

    const argumentText = readArgumentText(state, argumentNode);
    tooltipState.tooltip.create = () => {
        const { dom } = createTooltipView();
        const content = document.createElement("div");
        content.className = "ol-cm-command-tooltip-content";

        const name = document.createElement("div");
        name.className = "ol-cm-command-tooltip-name";
        name.textContent = commandName;
        content.appendChild(name);

        if (argumentText) {
            const argument = document.createElement("div");
            argument.className = "ol-cm-command-tooltip-argument";
            argument.textContent = argumentText;
            content.appendChild(argument);
        }

        const actions = document.createElement("div");
        actions.className = "ol-cm-command-tooltip-actions";

        if ((commandName === "HrefCommand" || commandName === "UrlCommand") && /^https?:\/\//i.test(argumentText)) {
            const link = document.createElement("a");
            link.className = "ol-cm-command-tooltip-link";
            link.href = argumentText;
            link.target = "_blank";
            link.rel = "noreferrer";
            link.textContent = "Open link";
            actions.appendChild(link);
        } else if (commandName === "Include" || commandName === "Input" || commandName === "Subfile") {
            const copyButton = document.createElement("button");
            copyButton.type = "button";
            copyButton.className = "ol-cm-command-tooltip-action";
            copyButton.textContent = "Copy path";
            copyButton.onclick = async () => {
                try {
                    await navigator.clipboard.writeText(argumentText);
                    copyButton.textContent = "Copied";
                } catch (_error) {
                    copyButton.textContent = "Copy failed";
                }
            };
            actions.appendChild(copyButton);
        }

        if (actions.childNodes.length > 0) {
            content.appendChild(actions);
        }

        dom.appendChild(content);
        return { dom };
    };

    return tooltipState;
}

function firstInteractiveElement(view, tooltipState) {
    return getTooltip(view, tooltipState.tooltip)?.dom.querySelector("input, button");
}

const closeCommandTooltipEffect = StateEffect.define();

export function closeCommandTooltip() {
    return {
        effects: closeCommandTooltipEffect.of(null),
    };
}

export const commandTooltipStateField = StateField.define({
    create(state) {
        return createTooltipState(state, null);
    },
    update(value, transaction) {
        if (transaction.effects.some((effect) => effect.is(closeCommandTooltipEffect))) {
            value = null;
        } else if (selectedCompletion(transaction.state)) {
            value = null;
        } else {
            if (value) {
                value.range = value.range.map(transaction.changes);
            }
            if (transaction.docChanged || transaction.selection) {
                value = createTooltipState(transaction.state, value);
            }
        }

        return value;
    },
    provide(field) {
        return [
            showTooltip.from(field, (value) => value ?? null),
            EditorView.decorations.from(field, (value) => {
                if (!value?.range) {
                    return Decoration.none;
                }

                return Decoration.set([
                    Decoration.mark({
                        attributes: {
                            "aria-haspopup": "menu",
                        },
                    }).range(value.range.from, value.range.to),
                ]);
            }),
        ];
    },
});

export const commandTooltipState = (state) => state.field(commandTooltipStateField, false);

export const resolveCommandNode = (state) => {
    const tooltipState = commandTooltipState(state);
    if (!tooltipState) {
        return null;
    }

    const pos = tooltipState.range.from;
    const tree = ensureSyntaxTree(state, pos);
    return tree ? tree.resolveInner(pos, 1).parent : null;
};

const commandTooltipKeymap = Prec.highest(
    keymap.of([
        {
            key: "Tab",
            run(view) {
                const tooltipState = commandTooltipState(view.state);
                if (tooltipState) {
                    const element = firstInteractiveElement(view, tooltipState);
                    if (element) {
                        if (element.type === "url" && element.value && hasNextSnippetField(view.state)) {
                            return false;
                        }
                        element.focus();
                        return true;
                    }
                }

                return false;
            },
        },
        {
            key: "Escape",
            run(view) {
                const tooltipState = commandTooltipState(view.state);
                if (tooltipState) {
                    view.dispatch(closeCommandTooltip());
                }

                return false;
            },
        },
    ]),
);

const commandTooltipTheme = EditorView.baseTheme({
    ".cm-tooltip.ol-cm-command-tooltip": {
        border: "1px solid #d6dde6",
        background: "#fffefc",
        color: "#22384d",
        boxShadow: "0 8px 24px rgba(24, 38, 56, 0.14)",
        padding: "6px 10px",
        fontFamily: '"SFMono-Regular", "Consolas", "Liberation Mono", monospace',
        fontSize: "12px",
    },
    ".ol-cm-command-tooltip-content": {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    ".ol-cm-command-tooltip-name": {
        fontWeight: "700",
        color: "#1f3348",
    },
    ".ol-cm-command-tooltip-argument": {
        color: "#51677d",
        wordBreak: "break-word",
    },
    ".ol-cm-command-tooltip-actions": {
        display: "flex",
        gap: "6px",
        marginTop: "4px",
    },
    ".ol-cm-command-tooltip-link, .ol-cm-command-tooltip-action": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "26px",
        padding: "0 8px",
        border: "1px solid #d2dce7",
        borderRadius: "6px",
        background: "#ffffff",
        color: "#314b61",
        fontSize: "11px",
        fontWeight: "700",
        textDecoration: "none",
        cursor: "pointer",
    },
});

export const commandTooltip = [commandTooltipStateField, commandTooltipKeymap, commandTooltipTheme];
